// Date helpers for the "Benutzerdefiniert" Zeitraum filter shared by the Tasks
// and Dashboard pages. Both pages own their own filter state and query wiring;
// everything date-related lives here so the two can't drift apart.

// Local calendar day as YYYY-MM-DD. Deliberately NOT toISOString() — that
// converts to UTC first and shifts the day for anything before 01:00/02:00
// Berlin time, which would silently mis-bucket early-morning calls.
export const isoDay = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const todayIso = () => isoDay(new Date());

// Parse YYYY-MM-DD as a LOCAL midnight. `new Date('2026-01-01')` would parse as
// UTC midnight, reintroducing the same off-by-one day shift.
const parseIso = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Inclusive on both ends: a range whose start equals its end is 1 day.
// Math.round absorbs the 23h/25h days at DST transitions.
export const daysBetweenInclusive = (fromIso, toIso) =>
  Math.round((parseIso(toIso) - parseIso(fromIso)) / 86400000) + 1;

// Single day, German. Native <input type="date"> renders in the BROWSER's locale
// (an en-US browser shows 07/30/2026 even under <html lang="de">), so the visible
// field is our own text input formatted here — the same overlay CalendarPage uses.
export const formatDeDay = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

// Compact German range for the closed trigger — shared parts are collapsed so
// the label stays inside the control (e.g. "01. – 31.01.2026").
export const formatDeRange = (fromIso, toIso) => {
  if (!fromIso || !toIso) return '';
  const [fy, fm, fd] = fromIso.split('-');
  const [ty, tm, td] = toIso.split('-');
  if (fromIso === toIso) return `${fd}.${fm}.${ty}`;
  if (fy === ty && fm === tm) return `${fd}. – ${td}.${tm}.${ty}`;
  if (fy === ty) return `${fd}.${fm}. – ${td}.${tm}.${ty}`;
  return `${fd}.${fm}.${fy} – ${td}.${tm}.${ty}`;
};

// Unabbreviated form for the `title` tooltip, where there's no width pressure.
export const formatDeRangeFull = (fromIso, toIso) => {
  if (!fromIso || !toIso) return '';
  const [fy, fm, fd] = fromIso.split('-');
  const [ty, tm, td] = toIso.split('-');
  return `${fd}.${fm}.${fy} – ${td}.${tm}.${ty}`;
};

// Drives both the Übernehmen button's enabled state and the drawer footer.
// `level` picks the footer's color: 'error'/'warn' coral, otherwise muted.
// A >366-day range is valid but warned about — the Dashboard trend chart draws
// one x-axis tick per day, and Tasks fetches every task back to `from`.
// ISO YYYY-MM-DD compares correctly as a plain string, so no parsing needed.
export const validateRange = (fromIso, toIso) => {
  if (!fromIso && !toIso) return { valid: false, level: 'hint', message: 'Von- und Bis-Datum wählen.' };
  if (!toIso) return { valid: false, level: 'hint', message: 'Bitte auch ein Bis-Datum wählen.' };
  if (!fromIso) return { valid: false, level: 'hint', message: 'Bitte auch ein Von-Datum wählen.' };
  if (toIso < fromIso) return { valid: false, level: 'error', message: 'Das Bis-Datum darf nicht vor dem Von-Datum liegen.' };
  const days = daysBetweenInclusive(fromIso, toIso);
  if (days > 366) return { valid: true, level: 'warn', message: 'Großer Zeitraum – das Laden kann etwas dauern.' };
  return { valid: true, level: null, message: `${days} ${days === 1 ? 'Tag' : 'Tage'}` };
};

// Quick presets the dropdown itself can't express: every built-in option is a
// CURRENT period or a rolling "letzte N Tage", so the whole PREVIOUS month or
// quarter — the natural question on the 1st — otherwise needs two hand-picked
// dates. Month index arithmetic rolls the year over on its own, and day 0 of a
// month is the last day of the one before it.
export const presetRange = (key) => {
  const now = new Date();
  if (key === 'lastMonth') {
    return {
      from: isoDay(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      to: isoDay(new Date(now.getFullYear(), now.getMonth(), 0)),
    };
  }
  const qStartMonth = Math.floor(now.getMonth() / 3) * 3;
  return {
    from: isoDay(new Date(now.getFullYear(), qStartMonth - 3, 1)),
    to: isoDay(new Date(now.getFullYear(), qStartMonth, 0)),
  };
};

// ─── Zeitraum presets ────────────────────────────────────────────────────────
// The preset list and its resolution live here because BOTH the Dashboard and
// the Tasks page offer the same Zeitraum control and must mean the same thing by
// it. They previously each owned a copy: the Dashboard resolved a token to
// inclusive calendar days, while Tasks resolved it to a rolling
// `now() - N*24h` lookback. The labels matched, the windows did not, so a
// Dashboard drill-through landed on a list that was a superset of the count that
// was clicked ("Heute" on Tasks reached back to this hour yesterday).
export const PERIOD_OPTIONS = [
  { value: '7', label: 'Letzte 7 Tage' },
  { value: '30', label: 'Letzte 30 Tage' },
  { value: '90', label: 'Letzte 90 Tage' },
  { value: 'today', label: 'Heute' },
  // Resolves through the numeric branch below (start = today - 1), so this needs
  // no case of its own — only a label that says what those two days are.
  { value: '2', label: 'Gestern & Heute' },
  { value: 'week', label: 'Diese Woche' },
  { value: 'month', label: 'Dieser Monat' },
  { value: 'quarter', label: 'Dieses Quartal' },
];

export const PERIOD_VALUES = PERIOD_OPTIONS.map(o => o.value);

// Resolve a Zeitraum token to an INCLUSIVE {from, to} pair of local calendar
// days. 'range' returns the caller's committed custom range, or null when that
// range is not usable yet. Every other token ends today; an unknown token falls
// back to the 7-day default rather than throwing.
export const resolvePeriodRange = (period, customRange) => {
  if (period === 'range') {
    const { from, to } = customRange || {};
    return from && to ? { from, to } : null;
  }
  const today = new Date();
  let start;
  const numeric = parseInt(period, 10);
  if (!isNaN(numeric)) {
    // "Letzte N Tage" counts today as day 1, so it starts N-1 days back.
    start = new Date(today);
    start.setDate(today.getDate() - (numeric - 1));
  } else if (period === 'today') {
    start = new Date(today);
  } else if (period === 'week') {
    // German week: Monday is day 1, Sunday closes the week.
    const dow = today.getDay(); // 0=Sun
    start = new Date(today);
    start.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1));
  } else if (period === 'month') {
    start = new Date(today.getFullYear(), today.getMonth(), 1);
  } else if (period === 'quarter') {
    start = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
  } else {
    start = new Date(today);
    start.setDate(today.getDate() - 6);
  }
  return { from: isoDay(start), to: isoDay(today) };
};
